<template>
  <div class="text-center">
    <v-dialog max-width="600px" v-model="dialog" transition="dialog-bottom-transition" overlay-opacity="0.9" overlay-color="black">
      <template v-slot:activator="{ on, attrs }">
        <v-btn icon large v-bind="attrs" v-on="on">
          <v-icon color="white">mdi-calendar-heart</v-icon>
        </v-btn>
      </template>

      <v-card class="rounded-xl" color="#1E1E1E">
        <v-toolbar flat color="transparent" class="pt-2">
          <v-toolbar-title class="text-h6 font-weight-bold white--text pl-2"> Freiwünsche </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false" color="grey"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <v-tabs v-model="tab" background-color="transparent" grow slider-color="#00963f" centered>
            <v-tab class="font-weight-bold text-caption white--text">Meine Wünsche</v-tab>
            <v-tab class="font-weight-bold text-caption white--text">Neuer Antrag</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab" class="transparent-bg mt-4" touchless>
            <v-tab-item>
              <div v-if="sortedFreeWishes.length === 0" class="pa-10 text-center">
                <v-icon size="64" color="#333" class="mb-4">mdi-calendar-blank</v-icon>
                <div class="grey--text">Keine offenen Freiwünsche.</div>
                <v-btn text color="#00963f" small @click="tab = 1" class="mt-2">Jetzt einen erstellen</v-btn>
              </div>

              <v-list v-else two-line color="transparent" class="px-2">
                <v-card v-for="freeWish of sortedFreeWishes" :key="freeWish._id" class="mb-3 rounded-lg border-light" color="#252525" elevation="0">
                  <div class="d-flex align-center pa-3">
                    <v-sheet color="#333" class="rounded-lg d-flex flex-column align-center justify-center mr-4" width="50" height="50">
                      <span class="font-weight-bold white--text text-h6 line-height-1">{{ $moment(freeWish.day).format("DD") }}</span>
                      <span class="text-uppercase grey--text text--lighten-1 text-xs">{{ $moment(freeWish.day).format("MMM") }}</span>
                    </v-sheet>

                    <div class="flex-grow-1 overflow-hidden">
                      <div class="text-subtitle-2 white--text text-truncate">
                        {{ freeWish.period === "full" ? "Ganzer Tag" : freeWish.period === "morning" ? "Vormittag" : "Nachmittag" }}
                      </div>
                      <div class="text-caption grey--text text-truncate">
                        {{ freeWish.comment || "Keine Bemerkung" }}
                      </div>
                    </div>

                    <div class="text-right d-flex flex-column align-end">
                      <v-chip
                        small
                        label
                        :color="freeWish.status === 'open' ? 'orange' : freeWish.status === 'accepted' ? '#00963f' : 'red'"
                        text-color="white"
                        class="font-weight-bold mb-1">
                        {{ mapHolidayStatus[freeWish.status] }}
                      </v-chip>

                      <v-btn icon x-small v-if="freeWish.status === 'open'" @click="selfCancelFreeWish(freeWish._id)" color="grey darken-1">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card>
              </v-list>
            </v-tab-item>

            <v-tab-item>
              <v-stepper v-model="stepperModel" vertical class="transparent elevation-0 pb-4">
                <v-stepper-step :complete="stepperModel > 1" step="1" color="#00963f">
                  <span class="white--text">Datum wählen</span>
                  <small v-if="stepperModel > 1" class="primary-green--text font-weight-bold">{{ $moment(day).format("DD.MM.YYYY") }}</small>
                </v-stepper-step>

                <v-stepper-content step="1">
                  <v-date-picker
                    v-model="day"
                    no-title
                    scrollable
                    color="#00963f"
                    full-width
                    dark
                    class="rounded-lg elevation-0 mb-4"></v-date-picker>
                  <v-btn color="#00963f" depressed class="rounded-lg white--text" @click="stepperModel = 2">Weiter</v-btn>
                </v-stepper-content>

                <v-stepper-step :complete="stepperModel > 2" step="2" color="#00963f">
                  <span class="white--text">Zeitraum</span>
                </v-stepper-step>

                <v-stepper-content step="2">
                  <v-item-group v-model="period" mandatory class="d-flex gap-2 mb-6">
                    <v-row dense>
                      <v-col
                        cols="4"
                        v-for="opt in [
                          { v: 'full', t: 'Ganzer Tag' },
                          { v: 'morning', t: 'Vormittag' },
                          { v: 'afternoon', t: 'Nachmittag' },
                        ]"
                        :key="opt.v">
                        <v-card
                          :color="period === opt.v ? '#00963f' : '#333'"
                          @click="period = opt.v"
                          class="py-4 text-center rounded-lg cursor-pointer transition-swing"
                          elevation="0">
                          <div class="text-caption font-weight-bold" :class="period === opt.v ? 'white--text' : 'grey--text'">{{ opt.t }}</div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-item-group>
                  <v-btn text color="grey" @click="stepperModel -= 1">Zurück</v-btn>
                  <v-btn color="#00963f" depressed class="rounded-lg white--text ml-2" @click="stepperModel = 3">Weiter</v-btn>
                </v-stepper-content>

                <v-stepper-step :complete="stepperModel > 3" step="3" color="#00963f">
                  <span class="white--text">Bemerkung (Optional)</span>
                </v-stepper-step>

                <v-stepper-content step="3">
                  <v-text-field v-model="comment" placeholder="Grund (optional)" solo flat background-color="#333" dark rounded></v-text-field>
                  <div class="d-flex mt-2">
                    <v-btn text color="grey" @click="stepperModel -= 1">Zurück</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn :loading="!buttonEnabled" color="#00963f" depressed class="rounded-lg white--text px-6" @click="saveFreeWish">
                      Absenden <v-icon right small>mdi-send</v-icon>
                    </v-btn>
                  </div>
                </v-stepper-content>
              </v-stepper>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"
export default {
  name: "EnterFreeWishes",
  data() {
    return {
      buttonEnabled: true,
      freeWishes: [],
      mapHolidayStatus: {
        open: "Offen",
        accepted: "Akzeptiert",
        rejected: "Abgelehnt",
      },
      tab: null,
      stepperModel: 1,
      day: this.$moment().format("YYYY-MM-DD"),
      dialog: false,
      period: "full",
      comment: "",
    }
  },
  async created() {
    this.freeWishes = await api.fetchFreeWishEntriesEmployee()
  },
  methods: {
    async selfCancelFreeWish(id) {
      if (!confirm("Bist du sicher, dass du diese Anfrage zurückziehen möchtest?")) return
      try {
        await api.changeFreeWishStatus({ _id: id, status: "rejected" })
        this.freeWishes = await api.fetchFreeWishEntriesEmployee()
      } catch (e) {
        this.$toast.error("Fehler beim Abbrechen")
      }
    },
    async saveFreeWish() {
      this.buttonEnabled = false
      try {
        let newEntry = {
          day: this.day,
          status: "open",
          period: this.period,
          comment: this.comment,
        }
        await api.createFreeWishEntryEmployee(newEntry)
        this.$toast.success("Freiwunsch gesendet")
        this.tab = 0
        this.comment = ""
        this.period = "full"
        this.stepperModel = 1
        this.freeWishes = await api.fetchFreeWishEntriesEmployee()
      } catch (e) {
        console.log(e)
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.buttonEnabled = true
      }
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    // --- NEU: SORTIERUNG ---
    sortedFreeWishes() {
      const wishes = [...this.freeWishes]
      return wishes.sort((a, b) => new Date(b.day) - new Date(a.day))
    },
  },
}
</script>

<style scoped>
.transparent-bg {
  background: transparent !important;
}
.text-xs {
  font-size: 10px;
}
.line-height-1 {
  line-height: 1;
}
.border-light {
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.primary-green--text {
  color: #00963f !important;
}
</style>
